import { isFixtureMode } from "./memberService";
import { invokeFunction, supabase } from "./supabase";

export type LaunchFlag = {
  key: string;
  enabled: boolean;
  description: string;
};

export type SopVersion = {
  id: string;
  documentId: string;
  title: string;
  version: number;
  status: string;
  sourceUrl?: string;
  createdAt: string;
};

export type WorkflowHealth = {
  total: number;
  live: number;
  invalid: number;
  paused: number;
  outboxPending: number;
  outboxFailed: number;
};

async function fixtures() {
  if (!import.meta.env.DEV) throw new Error("Development fixtures are unavailable in production.");
  return import("./operationsFixtures");
}

export async function listLaunchFlags(): Promise<LaunchFlag[]> {
  if (isFixtureMode) return structuredClone((await fixtures()).fixtureFlags);
  const { data, error } = await supabase!.from("feature_flags")
    .select("key,enabled,description").order("key");
  if (error) throw error;
  return data ?? [];
}

export async function setLaunchFlag(key: string, enabled: boolean) {
  if (isFixtureMode) {
    (await fixtures()).setFixtureFlag(key, enabled);
    return;
  }
  const { error } = await supabase!.from("feature_flags")
    .update({ enabled, updated_at: new Date().toISOString() }).eq("key", key);
  if (error) throw error;
}

export async function listSopVersions(): Promise<SopVersion[]> {
  if (isFixtureMode) return (await fixtures()).fixtureSopVersions;
  const { data, error } = await supabase!.from("sop_document_versions")
    .select("id,document_id,version,status,created_at,sop_documents(title,source_url)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => {
    const document = Array.isArray(row.sop_documents) ? row.sop_documents[0] : row.sop_documents;
    return {
      id: row.id, documentId: row.document_id, version: row.version, status: row.status,
      title: document?.title ?? "Untitled SOP", sourceUrl: document?.source_url ?? undefined,
      createdAt: row.created_at,
    };
  });
}

export async function approveSopVersion(versionId: string, payload: unknown) {
  if (isFixtureMode) return { playbookVersionId: (await fixtures()).fixtureSopVersions[0].id };
  return invokeFunction<{ playbookVersionId: string }>("approve-sop-version", { versionId, payload });
}

export async function getWorkflowHealth(): Promise<WorkflowHealth> {
  if (isFixtureMode) return (await fixtures()).fixtureWorkflowHealth;
  const [workflows, outbox] = await Promise.all([
    supabase!.from("workflows").select("status"),
    supabase!.from("integration_outbox").select("status"),
  ]);
  if (workflows.error) throw workflows.error;
  if (outbox.error) throw outbox.error;
  return {
    total: workflows.data?.length ?? 0,
    live: workflows.data?.filter((item) => item.status === "live").length ?? 0,
    invalid: workflows.data?.filter((item) => item.status === "invalid").length ?? 0,
    paused: workflows.data?.filter((item) => item.status === "paused").length ?? 0,
    outboxPending: outbox.data?.filter((item) => ["pending", "processing"].includes(item.status)).length ?? 0,
    outboxFailed: outbox.data?.filter((item) => item.status === "failed").length ?? 0,
  };
}
